/**
 * Redeem Group Form Modal Component
 * Modal for creating and editing redeem group definitions
 * Supports Zod validation and create/edit modes
 */

import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { type RedeemGroup } from '../../lib/types/redeemGroup';
import {
  useCreateRedeemGroup,
  useUpdateRedeemGroup,
} from '../../hooks/useRedeemGroupApi';
import { FormButton } from '../form/FormButton';
import { AnimatedMessage } from '../ui/AnimatedMessage';
import { cn } from '../../lib/utils';
import { BadgeSelector } from './BadgeSelector';
import { EntitlementSelector } from './EntitlementSelector';

interface RedeemGroupFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  group?: RedeemGroup | null; // null for create, RedeemGroup for edit
}

// Form input type (what the form uses internally)
interface RedeemGroupFormInput {
  name: string;
  entitlementIds: string[]; // Array of entitlement IDs (selected via EntitlementSelector)
  badgeIds: string[]; // Array of badge IDs (selected via BadgeSelector)
  enabled: boolean;
}

// Redeem group validation schema
const redeemGroupFormSchema = z.object({
  name: z
    .string()
    .min(3, 'Name must be at least 3 characters')
    .max(100, 'Name must be at most 100 characters'),
  entitlementIds: z.array(z.string()),
  badgeIds: z.array(z.string()),
  enabled: z.boolean(),
});

export function RedeemGroupFormModal({
  isOpen,
  onClose,
  group,
}: RedeemGroupFormModalProps) {
  const isEditMode = !!group;

  const createGroup = useCreateRedeemGroup();
  const updateGroup = useUpdateRedeemGroup();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    control,
    formState: { errors, isValid },
  } = useForm<RedeemGroupFormInput>({
    resolver: zodResolver(redeemGroupFormSchema),
    defaultValues: {
      name: group?.name ?? '',
      entitlementIds: group?.entitlementIds ?? [],
      badgeIds: group?.badgeIds ?? [],
      enabled: group?.enabled ?? true,
    },
    mode: 'onChange',
  });

  // Reset form when modal opens/closes or group changes
  useEffect(() => {
    if (isOpen) {
      reset({
        name: group?.name ?? '',
        entitlementIds: group?.entitlementIds ?? [],
        badgeIds: group?.badgeIds ?? [],
        enabled: group?.enabled ?? true,
      });
    }
  }, [isOpen, group, reset]);

  const onSubmit = async (data: RedeemGroupFormInput) => {
    try {
      // Both entitlementIds and badgeIds are already arrays
      if (isEditMode) {
        // Update existing group
        await updateGroup.mutateAsync({
          groupId: group.groupId,
          updates: {
            name: data.name,
            entitlementIds: data.entitlementIds,
            badgeIds: data.badgeIds,
            enabled: data.enabled,
          },
        });
      } else {
        // Create new group
        await createGroup.mutateAsync({
          name: data.name,
          entitlementIds: data.entitlementIds,
          badgeIds: data.badgeIds,
          enabled: data.enabled,
        });
      }

      reset();
      onClose();
    } catch (_error) {
      // Error is handled by the mutation
    }
  };

  const handleClose = () => {
    if (createGroup.isPending || updateGroup.isPending) return;
    reset();
    onClose();
  };

  if (!isOpen) return null;

  const isLoading = createGroup.isPending || updateGroup.isPending;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={isLoading ? undefined : handleClose}
        />

        {/* Modal */}
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="redeem-group-modal-title"
          className="inline-block w-full max-w-lg p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3
              id="redeem-group-modal-title"
              className="text-lg font-medium text-gray-900 dark:text-gray-100"
            >
              {isEditMode ? 'Edit Redeem Group' : 'Create New Redeem Group'}
            </h3>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 disabled:opacity-50"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Group Name */}
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Group Name *
              </label>
              <input
                {...register('name')}
                id="name"
                type="text"
                placeholder="e.g., Beta Tester Codes"
                disabled={isLoading}
                className={cn(
                  'block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:border-sky-500 focus:ring-sky-500 sm:text-sm',
                  errors.name &&
                    'border-red-300 focus:border-red-500 focus:ring-red-500'
                )}
              />
              <div className="flex justify-between mt-1">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Display name for the redeem group
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {watch('name')?.length || 0}/100
                </p>
              </div>
              {errors.name && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.name.message}
                </p>
              )}
            </div>

            {/* Entitlement IDs */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Entitlements
              </label>
              <Controller
                name="entitlementIds"
                control={control}
                render={({ field }) => (
                  <EntitlementSelector
                    selectedEntitlementIds={field.value}
                    onChange={field.onChange}
                    disabled={isLoading}
                  />
                )}
              />
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Select entitlements to grant when code is redeemed
              </p>
              {errors.entitlementIds && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.entitlementIds.message}
                </p>
              )}
            </div>

            {/* Badge IDs */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Badges
              </label>
              <Controller
                name="badgeIds"
                control={control}
                render={({ field }) => (
                  <BadgeSelector
                    selectedBadgeIds={field.value}
                    onChange={field.onChange}
                    disabled={isLoading}
                  />
                )}
              />
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Select badges to grant when code is redeemed
              </p>
              {errors.badgeIds && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.badgeIds.message}
                </p>
              )}
            </div>

            {/* Enabled Checkbox */}
            <div className="flex items-center">
              <input
                {...register('enabled')}
                id="enabled-checkbox"
                type="checkbox"
                disabled={isLoading}
                className="w-4 h-4 text-sky-600 border-gray-300 dark:border-gray-600 rounded focus:ring-sky-500 dark:bg-gray-700"
              />
              <label
                htmlFor="enabled-checkbox"
                className="ml-2 block text-sm text-gray-700 dark:text-gray-300"
              >
                Enabled (codes can be redeemed)
              </label>
            </div>

            {/* Error Messages */}
            {createGroup.error || updateGroup.error ? (
              <AnimatedMessage
                type="error"
                message={
                  createGroup.error instanceof Error
                    ? createGroup.error.message
                    : updateGroup.error instanceof Error
                      ? updateGroup.error.message
                      : 'An error occurred'
                }
              />
            ) : null}

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-4">
              <FormButton
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
                className="flex-1"
              >
                Cancel
              </FormButton>
              <FormButton
                type="submit"
                variant="primary"
                disabled={!isValid || isLoading}
                isLoading={isLoading}
                loadingText={isEditMode ? 'Updating...' : 'Creating...'}
                className="flex-1"
              >
                {isEditMode ? 'Update Group' : 'Create Group'}
              </FormButton>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
